import { useMemo, useState } from 'react';
import { Button } from 'antd';
import clsx from 'clsx';

import { IconCommentReply, IconDislike, IconLike } from '../Icon';
import AnimatedHeight from '../AnimatedHeight';
import CommentForm from '../Comments/CommentForm';
import { Reaction } from '../../../store/types';
import { CommentCreateParams } from '../../../store/comment/create';

import styles from './index.module.scss';

interface ReactionsProps {
  commentCreateParams: CommentCreateParams;
  reactions: Reaction[];
  numberOfLikes: number;
  numberOfDislikes: number;
  onLike: () => void;
  onDislike: () => void;
  removeReaction: (id: string) => void;
  hideReply?: boolean;
  onSubmitNewComment: () => void;
  disabled?: boolean; // guarded by parent (cooldown/in-flight/loading)
}

const Reactions = ({
  commentCreateParams,
  reactions,
  onLike,
  onDislike,
  removeReaction,
  numberOfLikes,
  numberOfDislikes,
  hideReply,
  onSubmitNewComment,
  disabled = false,
}: ReactionsProps) => {
  const [isOpenReply, setIsOpenReply] = useState(false);

  // Get current reaction if present
  const current = useMemo(() => reactions?.[0], [reactions]);
  const isLiked = current?.status === true;
  const isDisliked = current?.status === false;

  return (
    <div className={styles.wrapper}>
      <div className={styles.actions}>
        <Button
          type="text"
          className={clsx(styles.action, styles.count)}
          onClick={() => {
            if (isLiked && current?.id) {
              removeReaction(current.id);
            } else {
              onLike(); // call without event
            }
          }}
          disabled={disabled}
          aria-pressed={isLiked}
        >
          <IconLike
            className={clsx(
              styles.actionIcon,
              isLiked && styles.actionIconActive
            )}
          />
          {numberOfLikes || 0}
        </Button>

        <Button
          type="text"
          className={clsx(styles.action, styles.count)}
          onClick={() => {
            if (isDisliked && current?.id) {
              removeReaction(current.id);
            } else {
              onDislike(); // call without event
            }
          }}
          disabled={disabled}
          aria-pressed={isDisliked}
        >
          <IconDislike
            className={clsx(
              styles.actionIcon,
              isDisliked && styles.actionIconActive
            )}
          />
          {numberOfDislikes || 0}
        </Button>

        {!hideReply && (
          <Button
            type="text"
            className={clsx(styles.action, styles.reply)}
            onClick={() => setIsOpenReply((prev) => !prev)}
            disabled={disabled}
          >
            <IconCommentReply className={styles.actionIcon} />
            Reply
          </Button>
        )}
      </div>

      <AnimatedHeight isOpen={isOpenReply}>
        <CommentForm
          createParams={commentCreateParams}
          onSuccess={onSubmitNewComment}
        />
      </AnimatedHeight>
    </div>
  );
};

export default Reactions;
